/**
 * Add Website Modal component
 * Dialog for adding a new website to the current workspace
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';

interface AddWebsiteModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (websiteData: {
    domain: string;
    name: string;
    description?: string;
  }) => Promise<void>;
}

export default function AddWebsiteModal({ open, onClose, onCreate }: AddWebsiteModalProps) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!domain.trim()) {
      setError(t('addWebsite.errors.domainRequired', 'Domain is required'));
      return;
    }

    if (!name.trim()) {
      setError(t('addWebsite.errors.nameRequired', 'Website name is required'));
      return;
    }

    if (name.trim().length < 2) {
      setError(t('addWebsite.errors.nameTooShort', 'Website name must be at least 2 characters'));
      return;
    }

    // Validate domain format with proper TLD check
    let inputDomain = domain.trim();

    // For validation, remove protocol temporarily
    let domainForValidation = inputDomain.toLowerCase().replace(/^https?:\/\//, '');

    // Domain must have at least one dot and a valid TLD
    // Allow: example.com, blog.example.com, www.example.nl, localhost
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$|^localhost$/;

    if (!domainRegex.test(domainForValidation)) {
      setError(t('addWebsite.errors.invalidDomain', 'Please enter a valid domain (e.g., example.com, blog.example.nl)'));
      return;
    }

    // Ensure it has a proper format
    if (domainForValidation.includes(' ')) {
      setError(t('addWebsite.errors.noSpaces', 'Domain cannot contain spaces'));
      return;
    }

    // Validate description length if provided
    if (description.trim() && description.trim().length < 20) {
      setError(t('addWebsite.errors.descriptionTooShort', 'Description must be at least 20 characters'));
      return;
    }

    if (description.trim() && description.trim().length > 300) {
      setError(t('addWebsite.errors.descriptionTooLong', 'Description must be less than 300 characters'));
      return;
    }

    setIsCreating(true);

    try {
      // Send the domain WITH protocol if provided, backend will normalize it
      await onCreate({
        domain: inputDomain, // Keep protocol if user provided it
        name: name.trim(),
        description: description.trim() || undefined,
      });

      // Show success message
      setSuccess(true);
      setError('');

      // Auto-close after 1.5 seconds
      setTimeout(() => {
        setDomain('');
        setName('');
        setDescription('');
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('addWebsite.errors.failed', 'Failed to add website'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setDomain('');
      setName('');
      setDescription('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in-0" />

        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-full max-w-md
            bg-white dark:bg-gray-900
            border-2 border-black dark:border-white
            rounded-lg shadow-brutal
            p-6
            z-50
            animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2
          "
        >
          <Dialog.Title className="text-2xl font-bold mb-2 dark:text-white">
            {t('addWebsite.title', 'Add new website')}
          </Dialog.Title>

          <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('addWebsite.description', 'Add a new website to manage in this workspace')}
          </Dialog.Description>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('addWebsite.domainLabel', 'Domain') + ' *'}
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              disabled={isCreating}
              autoFocus
              required
            />

            <Input
              label={t('addWebsite.nameLabel', 'Website Name') + ' *'}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome Website"
              disabled={isCreating}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('addWebsite.descriptionLabel', 'Description')}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('addWebsite.descriptionPlaceholder', 'Briefly describe your website...')}
                rows={3}
                disabled={isCreating}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('addWebsite.descriptionHelper', 'Optional (20-300 characters)')}
              </p>
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 p-3 border-2 border-red-600 dark:border-red-400 rounded-lg bg-red-50 dark:bg-red-900/20">
                {error}
              </div>
            )}

            {success && (
              <div className="text-sm text-green-600 dark:text-green-400 p-3 border-2 border-green-600 dark:border-green-400 rounded-lg bg-green-50 dark:bg-green-900/20">
                ✓ {t('addWebsite.success', 'Website added successfully!')}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isCreating}
              >
                {t('common.cancel', 'Cancel')}
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={isCreating}
              >
                {isCreating
                  ? t('addWebsite.creating', 'Adding...')
                  : t('addWebsite.create', 'Add website')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
